import { SupabaseClient } from '@supabase/supabase-js';
import { supabase as supabaseClient } from '../client/browser';

export interface TherapyGroupData {
  id: string;
  name: string;
  groupType: 'COUPLES' | 'GROUP' | 'FAMILY';
  timezone?: string;
  billingType?: 'INDIVIDUAL' | 'SHARED';
  primaryPayerUserId?: string;
  leadTherapistId: string;
  coTherapistId?: string;
  status: 'INTAKE' | 'ACTIVE' | 'HOLD' | 'DISCHARGE_PLANNING' | 'COMPLETED' | 'WITHDRAWN' | 'TRANSFERRED';
  expectedFrequency?: 'MULTIPLE_PER_WEEK' | 'WEEKLY' | 'BIWEEKLY' | 'TRIWEEKLY' | 'MONTHLY' | 'AS_NEEDED' | null;
  createdAt: string;
  updatedAt: string;
}

export interface TherapyGroupWithEpisodes extends TherapyGroupData {
  episodes: Array<{
    id: string;
    userId: string;
    status: string;
    isContinue?: boolean | null;
    transitionFeedback?: string | null;
    clientFeedbackForPro?: string | null;
    pauseReason?: string | null;
    user?: {
      id: string;
      firstName?: string;
      lastName?: string;
      preferredName?: string;
      email?: string;
      phone?: string;
      birthday?: string;
      gender?: string;
      pronoun?: string;
      emergencyName?: string;
      emergencyPhone?: string;
      stripeCustomerId?: string;
    };
  }>;
  leadTherapist?: {
    id: string;
    firstName?: string;
    lastName?: string;
    email?: string;
  };
  coTherapist?: {
    id: string;
    firstName?: string;
    lastName?: string;
    email?: string;
  };
}

/**
 * Therapy Group service for managing therapy groups (couples, family, group therapy)
 */
export const therapyGroupService = {
  /**
   * Get therapy group by ID with all related episodes and users
   */
  async getByIdWithEpisodes(groupId: string, client?: SupabaseClient): Promise<TherapyGroupWithEpisodes | null> {
    const supabase = client || supabaseClient;
    
    const { data, error } = await supabase
      .from('therapy_groups')
      .select(`
        *,
        episodes (
          *,
          user:users!episodes_userId_fkey (
            id,
            firstName,
            lastName,
            preferredName,
            email,
            phone,
            birthday,
            gender,
            pronoun,
            emergencyName,
            emergencyPhone,
            stripeCustomerId,
            timezone,
            unit,
            street,
            city,
            province,
            country,
            postalCode
          )
        ),
        leadTherapist:pros!therapy_groups_leadTherapistId_fkey (
          id,
          firstName,
          lastName,
          email
        ),
        coTherapist:pros!therapy_groups_coTherapistId_fkey (
          id,
          firstName,
          lastName,
          email
        )
      `)
      .eq('id', groupId)
      .single();
    
    if (error) {
      console.error('Error fetching therapy group with episodes:', error);
      return null;
    }
    
    return data as TherapyGroupWithEpisodes;
  },

  /**
   * Get therapy group by episode ID
   */
  async getByEpisodeId(episodeId: string, client?: SupabaseClient): Promise<TherapyGroupWithEpisodes | null> {
    const supabase = client || supabaseClient;
    
    // First get the episode to find the groupId
    const { data: episode, error: episodeError } = await supabase
      .from('episodes')
      .select('groupId')
      .eq('id', episodeId)
      .single();
    
    if (episodeError || !episode?.groupId) {
      console.error('Error fetching episode or no groupId found:', episodeError);
      return null;
    }
    
    // Now fetch the therapy group with all episodes
    return await this.getByIdWithEpisodes(episode.groupId, client);
  },

  /**
   * Check if an episode belongs to a couples therapy group
   */
  async isCouplesTherapy(episodeId: string, client?: SupabaseClient): Promise<boolean> {
    const supabase = client || supabaseClient;
    
    const { data, error } = await supabase
      .from('episodes')
      .select(`
        groupId,
        therapy_groups!episodes_groupId_fkey (
          groupType
        )
      `)
      .eq('id', episodeId)
      .single();

    if (error || !data) {
      console.error('Error checking if couples therapy:', error);
      return false;
    }

    // Handle both array and single object cases
    const therapyGroups = data.therapy_groups as any;
    if (Array.isArray(therapyGroups)) {
      return therapyGroups[0]?.groupType === 'COUPLES';
    }
    return therapyGroups?.groupType === 'COUPLES';
  },

  /**
   * Get all episodes for a therapy group
   */
  async getEpisodesByGroupId(groupId: string, client?: SupabaseClient): Promise<any[]> {
    const supabase = client || supabaseClient;
    
    const { data, error } = await supabase
      .from('episodes')
      .select(`
        *,
        user:users!episodes_userId_fkey (
          id,
          firstName,
          lastName,
          preferredName,
          email,
          phone,
          birthday,
          gender,
          pronoun,
          emergencyName,
          emergencyPhone,
          stripeCustomerId
        )
      `)
      .eq('groupId', groupId)
      .order('createdAt', { ascending: true });
    
    if (error) {
      console.error('Error fetching episodes for group:', error);
      return [];
    }
    
    return data || [];
  },

  /**
   * Get all active therapy groups for a therapist (optimized for client selection)
   */
  async getActiveGroupsByTherapist(therapistId: string, client?: SupabaseClient): Promise<any[]> {
    const supabase = client || supabaseClient;

    // Fetch groups with episodes in a single query
    const { data, error } = await supabase
      .from('therapy_groups')
      .select(`
        id,
        name,
        groupType,
        status,
        episodes!inner (
          id,
          userId,
          status,
          user:users!episodes_userId_fkey (
            id,
            firstName,
            lastName,
            preferredName,
            email,
            phone
          )
        )
      `)
      .or(`leadTherapistId.eq.${therapistId},coTherapistId.eq.${therapistId}`)
      .in('status', ['ACTIVE', 'INTAKE', 'DISCHARGE_PLANNING'])
      .in('episodes.status', ['ACTIVE', 'INTAKE', 'DISCHARGE_PLANNING'])
      .order('name', { ascending: true });

    if (error) {
      console.error('Error fetching therapy groups for therapist:', error);
      return [];
    }

    return data || [];
  },

  /**
   * Get the therapy journey for a couples therapy group
   * This includes ALL couples therapy episodes and groups for both partners
   */
  async getCouplesTherapyJourney(groupId: string, client?: SupabaseClient): Promise<any> {
    const supabase = client || supabaseClient;
    
    // First get the therapy group with current episodes
    const therapyGroup = await this.getByIdWithEpisodes(groupId, client);
    if (!therapyGroup) return null;
    
    // Get all user IDs from current episodes (both partners)
    const userIds = therapyGroup.episodes.map(ep => ep.userId).filter(Boolean);
    if (userIds.length === 0) return { currentGroup: therapyGroup, therapyJourney: [] };
    
    
    // Fetch ALL episodes for these users to find all their therapy groups
    const { data: allEpisodes, error: episodesError } = await supabase
      .from('episodes')
      .select(`
        id,
        userId,
        groupId,
        therapyType,
        therapistId,
        startDate,
        endDate,
        status,
        therapist:pros!episodes_therapistId_fkey (
          id,
          firstName,
          lastName,
          email
        )
      `)
      .in('userId', userIds)
      .order('startDate', { ascending: false, nullsFirst: false });
    
    if (episodesError) {
      console.error('Error fetching all episodes:', episodesError);
      return { currentGroup: therapyGroup, therapyJourney: [] };
    }
    
    // Find all unique group IDs from couples episodes
    const couplesGroupIds = new Set<string>();
    for (const episode of allEpisodes || []) {
      if (episode.therapyType === 'COUPLES' && episode.groupId) {
        couplesGroupIds.add(episode.groupId);
      }
    }
    
    
    // Fetch all therapy groups with their episodes
    const therapyGroups = [];
    for (const gId of couplesGroupIds) {
      const { data: groupData, error: groupError } = await supabase
        .from('therapy_groups')
        .select(`
          *,
          leadTherapist:pros!therapy_groups_leadTherapistId_fkey (
            id,
            firstName,
            lastName
          ),
          episodes (
            id,
            userId,
            startDate,
            endDate,
            status,
            user:users!episodes_userId_fkey (
              id,
              firstName,
              lastName,
              preferredName
            )
          )
        `)
        .eq('id', gId)
        .single();
      
      if (!groupError && groupData) {
        // Check if both partners are in this group
        const episodeUserIds = groupData.episodes?.map((ep: any) => ep.userId) || [];
        const hasBothPartners = userIds.every(uid => episodeUserIds.includes(uid));
        
        if (hasBothPartners) {
          therapyGroups.push({
            groupId: groupData.id,
            groupName: groupData.name,
            therapist: groupData.leadTherapist,
            startDate: groupData.episodes?.reduce((earliest: string | null, ep: any) => {
              if (!earliest || (ep.startDate && ep.startDate < earliest)) {
                return ep.startDate;
              }
              return earliest;
            }, null),
            endDate: groupData.episodes?.reduce((latest: string | null, ep: any) => {
              if (!latest || (ep.endDate && ep.endDate > latest)) {
                return ep.endDate;
              }
              return latest;
            }, null),
            status: groupData.status,
            isCurrent: groupData.id === groupId,
            episodes: groupData.episodes,
            partners: groupData.episodes?.map((ep: any) => {
              const user = ep.user;
              return user ? `${user.preferredName || user.firstName || ''} ${user.lastName || ''}`.trim() : 'Unknown';
            }).filter(Boolean)
          });
        }
      }
    }
    
    // Sort therapy groups by date (most recent first, current always first)
    const therapyJourney = therapyGroups.sort((a, b) => {
      // Current group always first
      if (a.isCurrent) return -1;
      if (b.isCurrent) return 1;
      
      // Then sort by start date (most recent first)
      const dateA = new Date(a.startDate || '1970-01-01');
      const dateB = new Date(b.startDate || '1970-01-01');
      return dateB.getTime() - dateA.getTime();
    });
    
    
    return {
      currentGroup: therapyGroup,
      therapyJourney
    };
  },

  /**
   * Update therapy group status
   */
  async updateStatus(
    groupId: string,
    status: 'INTAKE' | 'ACTIVE' | 'HOLD' | 'DISCHARGE_PLANNING' | 'COMPLETED' | 'WITHDRAWN' | 'TRANSFERRED',
    client?: SupabaseClient
  ): Promise<void> {
    const supabase = client || supabaseClient;

    const { error } = await supabase
      .from('therapy_groups')
      .update({
        status,
        updatedAt: new Date().toISOString()
      })
      .eq('id', groupId);

    if (error) {
      console.error('Error updating therapy group status:', error);
      throw error;
    }
  },

  /**
   * Close couples therapy - updates all episodes and group to COMPLETED status
   */
  async closeCouplesTherapy(
    groupId: string,
    client?: SupabaseClient
  ): Promise<void> {
    const supabase = client || supabaseClient;

    // Get all episodes in this group
    const episodes = await this.getEpisodesByGroupId(groupId, client);

    // Update all episodes to COMPLETED
    const episodeUpdates = episodes.map(episode =>
      supabase
        .from('episodes')
        .update({ status: 'COMPLETED' })
        .eq('id', episode.id)
    );

    await Promise.all(episodeUpdates);

    // Update the therapy group to COMPLETED
    await this.updateStatus(groupId, 'COMPLETED', client);
  },

  /**
   * Delete a therapy group and ALL associated data including:
   * - All episodes in the group
   * - All sessions (both group sessions and episode sessions)
   * - All progress notes (both group notes and episode notes)
   * - All assessments (both group assessments and episode assessments)
   * - All wallet transactions for sessions
   * - All sent emails for the group
   *
   * HIPAA Critical: This is a destructive operation for audit purposes
   */
  async deleteGroupWithAllData(
    groupId: string,
    client?: SupabaseClient
  ): Promise<{
    success: boolean;
    deletedEpisodes: number;
    deletedSessions: number;
    deletedNotes: number;
    deletedAssessments: number;
    deletedWalletTransactions: number;
    deletedEmails: number;
    error?: string;
  }> {
    const supabase = client || supabaseClient;

    try {
      // 1. Get all episodes in this group
      const { data: episodes, error: episodesError } = await supabase
        .from('episodes')
        .select('id')
        .eq('groupId', groupId);

      if (episodesError) {
        console.error('Error fetching episodes for group:', episodesError);
        return {
          success: false,
          deletedEpisodes: 0,
          deletedSessions: 0,
          deletedNotes: 0,
          deletedAssessments: 0,
          deletedWalletTransactions: 0,
          deletedEmails: 0,
          error: episodesError.message
        };
      }

      const episodeIds = (episodes || []).map(e => e.id);
      const episodeCount = episodeIds.length;

      // 2. Get all sessions (both group sessions and episode sessions)
      const [groupSessionsResult, episodeSessionsResult] = await Promise.all([
        supabase.from('sessions').select('id').eq('groupId', groupId),
        episodeIds.length > 0
          ? supabase.from('sessions').select('id').in('episodeId', episodeIds)
          : Promise.resolve({ data: [], error: null })
      ]);

      const groupSessions = groupSessionsResult.data || [];
      const episodeSessions = episodeSessionsResult.data || [];

      // Combine and deduplicate session IDs
      const allSessionIds = [...new Set([
        ...groupSessions.map(s => s.id),
        ...episodeSessions.map(s => s.id)
      ])];
      const sessionCount = allSessionIds.length;

      // 3. Get all progress notes (both group notes and episode notes)
      const [groupNotesResult, episodeNotesResult] = await Promise.all([
        supabase.from('progress_notes').select('id').eq('groupId', groupId),
        episodeIds.length > 0
          ? supabase.from('progress_notes').select('id').in('episodeId', episodeIds)
          : Promise.resolve({ data: [], error: null })
      ]);

      const groupNotes = groupNotesResult.data || [];
      const episodeNotes = episodeNotesResult.data || [];

      const allNoteIds = [...new Set([
        ...groupNotes.map(n => n.id),
        ...episodeNotes.map(n => n.id)
      ])];
      const notesCount = allNoteIds.length;

      // 4. Get all assessments (both group assessments and episode assessments)
      const [groupAssessmentsResult, episodeAssessmentsResult] = await Promise.all([
        supabase.from('assessments').select('id').eq('groupId', groupId),
        episodeIds.length > 0
          ? supabase.from('assessments').select('id').in('episodeId', episodeIds)
          : Promise.resolve({ data: [], error: null })
      ]);

      const groupAssessments = groupAssessmentsResult.data || [];
      const episodeAssessments = episodeAssessmentsResult.data || [];

      const allAssessmentIds = [...new Set([
        ...groupAssessments.map(a => a.id),
        ...episodeAssessments.map(a => a.id)
      ])];
      const assessmentsCount = allAssessmentIds.length;

      // 5. Get sent emails for this group
      const { data: sentEmails } = await supabase
        .from('sent_emails')
        .select('id')
        .eq('groupId', groupId);

      const emailIds = (sentEmails || []).map(e => e.id);
      const emailsCount = emailIds.length;

      // 6. Delete wallet transactions BEFORE sessions (due to foreign key constraint)
      let walletTransactionsCount = 0;
      if (allSessionIds.length > 0) {
        const { data: walletTransactions } = await supabase
          .from('session_wallet_transactions')
          .select('id')
          .in('sessionId', allSessionIds);

        walletTransactionsCount = walletTransactions?.length || 0;

        if (walletTransactionsCount > 0) {
          const walletTxIds = walletTransactions!.map(tx => tx.id);
          const { error: deleteWalletTxError } = await supabase
            .from('session_wallet_transactions')
            .delete()
            .in('id', walletTxIds);

          if (deleteWalletTxError) {
            console.error('Error deleting wallet transactions:', deleteWalletTxError);
            return {
              success: false,
              deletedEpisodes: 0,
              deletedSessions: 0,
              deletedNotes: 0,
              deletedAssessments: 0,
              deletedWalletTransactions: 0,
              deletedEmails: 0,
              error: deleteWalletTxError.message
            };
          }
        }
      }

      // 7. Delete all sessions
      if (allSessionIds.length > 0) {
        const { error: deleteSessionsError } = await supabase
          .from('sessions')
          .delete()
          .in('id', allSessionIds);

        if (deleteSessionsError) {
          console.error('Error deleting sessions:', deleteSessionsError);
          return {
            success: false,
            deletedEpisodes: 0,
            deletedSessions: 0,
            deletedNotes: 0,
            deletedAssessments: 0,
            deletedWalletTransactions: walletTransactionsCount,
            deletedEmails: 0,
            error: deleteSessionsError.message
          };
        }
      }

      // 8. Delete all progress notes
      if (allNoteIds.length > 0) {
        const { error: deleteNotesError } = await supabase
          .from('progress_notes')
          .delete()
          .in('id', allNoteIds);

        if (deleteNotesError) {
          console.error('Error deleting progress notes:', deleteNotesError);
          return {
            success: false,
            deletedEpisodes: 0,
            deletedSessions: sessionCount,
            deletedNotes: 0,
            deletedAssessments: 0,
            deletedWalletTransactions: walletTransactionsCount,
            deletedEmails: 0,
            error: deleteNotesError.message
          };
        }
      }

      // 9. Delete all assessments
      if (allAssessmentIds.length > 0) {
        const { error: deleteAssessmentsError } = await supabase
          .from('assessments')
          .delete()
          .in('id', allAssessmentIds);

        if (deleteAssessmentsError) {
          console.error('Error deleting assessments:', deleteAssessmentsError);
          return {
            success: false,
            deletedEpisodes: 0,
            deletedSessions: sessionCount,
            deletedNotes: notesCount,
            deletedAssessments: 0,
            deletedWalletTransactions: walletTransactionsCount,
            deletedEmails: 0,
            error: deleteAssessmentsError.message
          };
        }
      }

      // 10. Delete sent emails
      if (emailIds.length > 0) {
        const { error: deleteEmailsError } = await supabase
          .from('sent_emails')
          .delete()
          .in('id', emailIds);

        if (deleteEmailsError) {
          console.error('Error deleting sent emails:', deleteEmailsError);
          // Continue anyway - not critical
        }
      }

      // 11. Delete all episodes
      if (episodeIds.length > 0) {
        const { error: deleteEpisodesError } = await supabase
          .from('episodes')
          .delete()
          .in('id', episodeIds);

        if (deleteEpisodesError) {
          console.error('Error deleting episodes:', deleteEpisodesError);
          return {
            success: false,
            deletedEpisodes: 0,
            deletedSessions: sessionCount,
            deletedNotes: notesCount,
            deletedAssessments: assessmentsCount,
            deletedWalletTransactions: walletTransactionsCount,
            deletedEmails: emailsCount,
            error: deleteEpisodesError.message
          };
        }
      }

      // 12. Finally delete the therapy group itself
      const { error: deleteGroupError } = await supabase
        .from('therapy_groups')
        .delete()
        .eq('id', groupId);

      if (deleteGroupError) {
        console.error('Error deleting therapy group:', deleteGroupError);
        return {
          success: false,
          deletedEpisodes: episodeCount,
          deletedSessions: sessionCount,
          deletedNotes: notesCount,
          deletedAssessments: assessmentsCount,
          deletedWalletTransactions: walletTransactionsCount,
          deletedEmails: emailsCount,
          error: deleteGroupError.message
        };
      }

      return {
        success: true,
        deletedEpisodes: episodeCount,
        deletedSessions: sessionCount,
        deletedNotes: notesCount,
        deletedAssessments: assessmentsCount,
        deletedWalletTransactions: walletTransactionsCount,
        deletedEmails: emailsCount
      };

    } catch (error) {
      console.error('Unexpected error in deleteGroupWithAllData:', error);
      return {
        success: false,
        deletedEpisodes: 0,
        deletedSessions: 0,
        deletedNotes: 0,
        deletedAssessments: 0,
        deletedWalletTransactions: 0,
        deletedEmails: 0,
        error: error instanceof Error ? error.message : 'Unknown error occurred'
      };
    }
  }
};